/**
 * SPARC-009: geographic zone RBAC helpers for Opportunities dashboard.
 * Mirrors backend COUNTRY_CODE_PATTERNS in country_access.py.
 */

export type GeographicZoneId =
  | 'world'
  | 'canada'
  | 'usa'
  | 'mexico'
  | 'africa'
  | 'brazil'
  | 'india';

export interface GeographicZone {
  id: GeographicZoneId;
  label: string;
  icon: string;
  accessCode?: string;
}

/** Catch-all — no geographic_zone param sent to API (country_access RBAC only). */
export const WORLD_ZONE: GeographicZone = {
  id: 'world',
  label: 'World',
  icon: '🌐',
};

// SPARC-ISSUE-004: labels use full, consistent country names (matching
// COUNTRY_ACCESS_OPTIONS below) rather than a mix of abbreviations ("US") and
// full names ("Canada", "Mexico") — keeps the Region toggle bar readable and
// consistent with role/RBAC copy shown elsewhere (e.g. account access lists).
export const GEOGRAPHIC_ZONES: GeographicZone[] = [
  { id: 'canada', label: 'Canada', icon: '🇨🇦', accessCode: 'CA' },
  { id: 'usa', label: 'United States', icon: '🇺🇸', accessCode: 'US' },
  { id: 'mexico', label: 'Mexico', icon: '🇲🇽', accessCode: 'MX' },
  { id: 'africa', label: 'Africa', icon: '🌍', accessCode: 'AF' },
  { id: 'brazil', label: 'Brazil', icon: '🇧🇷', accessCode: 'BR' },
  { id: 'india', label: 'India', icon: '🇮🇳', accessCode: 'IN' },
];

export const COUNTRY_ACCESS_OPTIONS = [
  { code: 'ALL', label: 'All regions (World)' },
  { code: 'CA', label: 'Canada' },
  { code: 'US', label: 'United States' },
  { code: 'MX', label: 'Mexico' },
  { code: 'IN', label: 'India' },
  { code: 'BR', label: 'Brazil' },
  { code: 'AF', label: 'Africa' },
] as const;

const COUNTRY_CODE_PATTERNS: Record<string, string[]> = {
  CA: ['canada'],
  US: ['united states', 'u.s.', 'usa', 'america'],
  MX: ['mexico'],
  IN: ['india', 'south asia'],
  BR: ['brazil'],
  AF: [
    'africa',
    'eastern and southern africa',
    'western and central africa',
    'kenya',
    'tanzania',
    'ethiopia',
    'zimbabwe',
    'cameroon',
    'botswana',
    'south africa',
    'ghana',
    'nigeria',
    'uganda',
    'rwanda',
  ],
};

export function normalizeCountryAccess(raw: unknown): string[] {
  if (raw == null) return [];
  if (typeof raw === 'string') {
    const token = raw.trim().toUpperCase();
    return token ? [token] : [];
  }
  if (Array.isArray(raw)) {
    return raw
      .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
      .map((item) => item.trim().toUpperCase());
  }
  return [];
}

export function countryAccessGrantsAll(access: string[] | null | undefined): boolean {
  const codes = normalizeCountryAccess(access);
  return codes.length === 0 || codes.includes('ALL');
}

export function decodeCountryAccessFromToken(accessToken: string | null | undefined): string[] {
  if (!accessToken || accessToken.split('.').length < 2) return [];
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    return normalizeCountryAccess(payload.country_access);
  } catch {
    return [];
  }
}

export function isZoneAccessible(zone: GeographicZone, access: string[] | null | undefined): boolean {
  if (zone.id === 'world') {
    return shouldShowWorldZone(access);
  }
  if (countryAccessGrantsAll(access)) return true;
  const codes = normalizeCountryAccess(access);
  return !!zone.accessCode && codes.includes(zone.accessCode);
}

export function getAccessibleZones(access: string[] | null | undefined): GeographicZone[] {
  if (countryAccessGrantsAll(access)) return GEOGRAPHIC_ZONES;
  const codes = new Set(normalizeCountryAccess(access));
  return GEOGRAPHIC_ZONES.filter((zone) => zone.accessCode && codes.has(zone.accessCode));
}

/** World toggle visible for ALL users or users with 2+ regional codes. */
export function shouldShowWorldZone(access: string[] | null | undefined): boolean {
  if (countryAccessGrantsAll(access)) return true;
  return getAccessibleZones(access).length > 1;
}

export interface ResolveGeographicZoneOptions {
  /** ALL-access landing zone from app config. Keystone/CSM omit this (World). */
  allAccessDefault?: GeographicZoneId;
}

export function isGeographicZoneId(value: string | null | undefined): value is GeographicZoneId {
  return value === 'world' || GEOGRAPHIC_ZONES.some((zone) => zone.id === value);
}

/** App-config landing zone for ALL-access users; invalid values fall back to World. */
export function resolveAllAccessDefaultZone(
  configured: string | null | undefined
): GeographicZoneId {
  return isGeographicZoneId(configured) ? configured : 'world';
}

export function getDefaultZone(
  access: string[] | null | undefined,
  options?: ResolveGeographicZoneOptions
): GeographicZoneId {
  if (countryAccessGrantsAll(access)) {
    return options?.allAccessDefault ?? 'world';
  }
  const accessible = getAccessibleZones(access);
  if (accessible.length === 1) return accessible[0].id;
  if (accessible.length > 1) return 'world';
  return 'world';
}

/** Read persisted zone id from localStorage (new + legacy keys). */
export function readStoredGeographicZone(): GeographicZoneId | null {
  try {
    const stored =
      localStorage.getItem('gov-data-geographic-zone') ??
      localStorage.getItem('external-data-dashboard-selected-geographic-zone');
    if (!stored) return null;
    const parsed = JSON.parse(stored) as GeographicZoneId;
    if (
      parsed === 'world' ||
      GEOGRAPHIC_ZONES.some((zone) => zone.id === parsed)
    ) {
      return parsed;
    }
  } catch {
    // ignore invalid storage
  }
  return null;
}

/**
 * Landing zone for Government Data. Restores a stored RBAC-accessible preference
 * (including ALL-access tenants after their first choice). SPARC sets allAccessDefault
 * to Canada; Keystone/CSM omit it and stay on World.
 */
export function resolveInitialGeographicZone(
  storedZone: GeographicZoneId | null | undefined,
  access: string[] | null | undefined,
  options?: ResolveGeographicZoneOptions
): GeographicZoneId {
  const defaultZone = getDefaultZone(access, options);
  if (!storedZone) {
    return defaultZone;
  }
  const zoneDef =
    storedZone === 'world'
      ? WORLD_ZONE
      : GEOGRAPHIC_ZONES.find((zone) => zone.id === storedZone) ?? WORLD_ZONE;
  return isZoneAccessible(zoneDef, access) ? storedZone : defaultZone;
}

export type GeographicZoneOpportunityCounts = Partial<
  Record<GeographicZoneId, number>
>;

/**
 * Country/region pills for the toggle bar. RBAC-accessible zones render
 * immediately, including empty catalogs. Counts are optional chrome.
 */
export function getZonesWithOpportunities(
  access: string[] | null | undefined,
  _counts?: GeographicZoneOpportunityCounts,
  _options?: { countsLoaded?: boolean }
): GeographicZone[] {
  return getAccessibleZones(access);
}

/** Whether a country pill should render. Counts are not a visibility gate. */
export function shouldShowGeographicZonePill(
  zoneId: GeographicZoneId,
  access: string[] | null | undefined,
  _counts?: GeographicZoneOpportunityCounts,
  _options?: { countsLoaded?: boolean }
): boolean {
  if (zoneId === 'world') {
    return shouldShowWorldZone(access);
  }
  const zone = GEOGRAPHIC_ZONES.find((z) => z.id === zoneId);
  if (!zone || !isZoneAccessible(zone, access)) {
    return false;
  }
  return true;
}

/** Policy-pack zone id for server-side geographic_zone filter; omit for World. */
export function getGeographicZoneFilterValue(zoneId: GeographicZoneId): string | undefined {
  if (zoneId === 'world') return undefined;
  return zoneId;
}

export function opportunityMatchesZone(
  countryRaw: string | null | undefined,
  zoneId: GeographicZoneId
): boolean {
  if (zoneId === 'world') return true;
  const zone = GEOGRAPHIC_ZONES.find((z) => z.id === zoneId);
  if (!zone?.accessCode) return true;
  const country = String(countryRaw || '').trim().toLowerCase();
  if (!country) return false;
  const patterns = COUNTRY_CODE_PATTERNS[zone.accessCode] ?? [zone.accessCode.toLowerCase()];
  if (patterns.some((pattern) => country.includes(pattern))) return true;
  return country === zone.accessCode.toLowerCase();
}

export function formatCountryAccessLabel(access: string[] | null | undefined): string {
  if (countryAccessGrantsAll(access)) return 'All regions';
  const codes = normalizeCountryAccess(access);
  if (!codes.length) return 'All regions';
  return codes
    .map((code) => COUNTRY_ACCESS_OPTIONS.find((o) => o.code === code)?.label ?? code)
    .join(', ');
}

/** Data sources shown per geographic zone on the Data Integrations tab (client-side until API supports geographic_zone). */
const GEOGRAPHIC_ZONE_DATA_SOURCES: Partial<Record<GeographicZoneId, readonly string[]>> = {
  canada: [
    'canada_grants',
    'canada_tenders',
    'bc_bid',
    'bids_tender',
    'bonfire',
    'merx',
    'sap_ariba',
    'jaggaer',
    'city_portals',
    'vancouver_city_portal',
    'victoria_city_portal',
    'metrovancouver_city_portal',
    'tendertiger_canada',
  ],
  usa: [
    'samgov',
    'usa_awards',
    'usa_budgets',
    'federal_register',
    'executive_orders',
    'rss_feeds',
    'tendertiger_usa',
  ],
  mexico: ['tendertiger_mexico', 'worldbank'],
  africa: [
    'tendertiger_ethiopia',
    'tendertiger_kenya',
    'tendertiger_south_africa',
    'tendertiger_tanzania',
    'worldbank',
  ],
  brazil: ['tendertiger_brazil', 'worldbank'],
  india: [
    'tender_247',
    'tendertiger_india',
    'eprocure',
    'gem',
    'hp_tenders',
    'tender_odisha',
    'uk_tenders',
  ],
};

/** Category heuristics per zone until per-feed geographic_zones tags ship (Phase 2). */
const ZONE_RSS_CATEGORIES: Partial<Record<GeographicZoneId, readonly string[]>> = {
  canada: ['mining', 'forestry', 'agriculture', 'commodity', 'geopolitics'],
  usa: [
    'procurement_usa',
    'government_contracts',
    'government_technology',
    'policy',
    'defense',
  ],
  mexico: ['geopolitics', 'commodity', 'mining'],
  africa: ['geopolitics', 'commodity', 'mining'],
  brazil: ['mining', 'commodity', 'agriculture'],
  india: ['technology', 'government_technology', 'mining', 'geopolitics'],
};

/** United States geographic zone — Federal Register and Executive Orders are US-only. */
export function isUnitedStatesGeographicZone(zoneId: GeographicZoneId): boolean {
  return zoneId === 'usa';
}

/** US Federal Register and Executive Orders — shown only when United States region is selected. */
export function supportsFederalRegisterTabs(zoneId: GeographicZoneId): boolean {
  return isUnitedStatesGeographicZone(zoneId);
}

/** @deprecated Use supportsFederalRegisterTabs — RSS loads for all zones via client-side feed filter. */
export function supportsIndustryNewsApi(zoneId: GeographicZoneId): boolean {
  return supportsFederalRegisterTabs(zoneId);
}

export function dataSourceMatchesGeographicZone(
  sourceId: string,
  zoneId: GeographicZoneId,
  access?: string[] | null
): boolean {
  if (zoneId === 'world') {
    if (countryAccessGrantsAll(access)) return true;
    const accessible = getAccessibleZones(access);
    if (accessible.length === 0) return true;
    return accessible.some((zone) => {
      const allowed = GEOGRAPHIC_ZONE_DATA_SOURCES[zone.id];
      return !allowed || allowed.includes(sourceId);
    });
  }
  const allowed = GEOGRAPHIC_ZONE_DATA_SOURCES[zoneId];
  if (!allowed) return true;
  return allowed.includes(sourceId);
}

/** Client-side RSS feed filter by category until news API accepts geographic_zone. */
export function rssFeedMatchesGeographicZone(
  feedCategory: string,
  zoneId: GeographicZoneId
): boolean {
  if (zoneId === 'world') return true;

  const category = feedCategory.toLowerCase();

  if (category === 'procurement_usa' && zoneId !== 'usa') {
    return false;
  }

  if (zoneId === 'usa') {
    return true;
  }

  const allowedCategories = ZONE_RSS_CATEGORIES[zoneId];
  if (!allowedCategories) return true;
  return allowedCategories.some(
    (allowed) => category === allowed || category.includes(allowed)
  );
}
